import React, { useState } from "react";
import {
    ChevronRight,
    TextFields,
    Numbers,
    ToggleOn,
    DataArray,
    DataObject,
    BlockOutlined,
    Add as AddIcon,
} from "@mui/icons-material";
import { Box, Stack, Typography, Tooltip, IconButton } from "@mui/material";
import { JsonSchema, JsonSchemaObject, JsonSchemaArray } from "./types";
import hexToRGB from "utils/hexToRGB";
import { SchemaPath } from "./constants";

interface SchemaHierarchyTreeProps {
    schema: JsonSchema;
    selectedPath: SchemaPath;
    onSelectPath: (path: SchemaPath) => void;
    onAddProperty?: (path: SchemaPath) => void;
    onAddItem?: (path: SchemaPath) => void;
}

interface TreeNodeProps {
    schema: JsonSchema;
    path: SchemaPath;
    label: React.ReactNode;
    onSelectPath: (path: SchemaPath) => void;
    selectedPath: SchemaPath;
    onAddProperty?: (path: SchemaPath) => void;
    onAddItem?: (path: SchemaPath) => void;
    depth?: number;
}

function getTypeLabel(type: string): string {
    const typeMap: Record<string, string> = {
        string: "String",
        number: "Number",
        integer: "Integer",
        boolean: "Boolean",
        array: "Array",
        object: "Object",
        null: "Null",
    };
    return typeMap[type] || type;
}

function getTypeIcon(type: string) {
    const iconMap: Record<string, React.ReactNode> = {
        string: <TextFields fontSize="inherit" color="inherit" />,
        number: <Numbers fontSize="inherit" color="inherit" />,
        integer: <Numbers fontSize="inherit" color="inherit" />,
        boolean: <ToggleOn fontSize="inherit" color="inherit" />,
        array: <DataArray fontSize="inherit" color="inherit" />,
        object: <DataObject fontSize="inherit" color="inherit" />,
        null: <BlockOutlined fontSize="inherit" color="inherit" />,
    };
    return iconMap[type] || null;
}

interface TreeNodeLabelProps {
    type: string;
    primaryText: string;
    secondaryText?: string;
}

function TreeNodeLabel({
    type,
    primaryText,
    secondaryText,
}: TreeNodeLabelProps) {
    return (
        <Stack
            direction="row"
            columnGap={1}
            alignItems="center"
            width="100%"
            flexWrap="nowrap"
        >
            <Tooltip title={getTypeLabel(type)}>
                <Stack
                    direction="row"
                    alignItems="center"
                    fontSize="16px"
                    color="text.secondary"
                >
                    {getTypeIcon(type)}
                </Stack>
            </Tooltip>
            <Stack
                direction="row"
                justifyContent="space-between"
                columnGap={1}
                alignItems="center"
                width="100%"
                flexWrap="nowrap"
            >
                <Typography>{primaryText}</Typography>
                {/* {secondaryText && (
                    <Typography
                        sx={(theme) => ({
                            color: theme.palette.gray[700],
                            fontSize: "11px",
                        })}
                    >
                        {secondaryText}
                    </Typography>
                )} */}
            </Stack>
        </Stack>
    );
}

function TreeNode({
    schema,
    path,
    label,
    onSelectPath,
    selectedPath,
    onAddProperty,
    onAddItem,
    depth = 0,
}: TreeNodeProps) {
    const [expanded, setExpanded] = useState(path.length === 0);
    const isSelected = JSON.stringify(path) === JSON.stringify(selectedPath);

    // Calculate padding based on depth (1 unit = 16px)
    const depthPadding = depth * 1;

    const isObject = schema.type === "object";
    const isArray = schema.type === "array";
    const canHaveChildren = isObject || isArray;

    const isObjectAndHasChildren =
        isObject &&
        schema.properties &&
        Object.keys(schema.properties ?? {}).length > 0;
    const isArrayAndHasChildren = isArray && !!schema.items;

    const hasChildren = isObjectAndHasChildren || isArrayAndHasChildren;

    return (
        <Box>
            <Box
                paddingInline={1}
                paddingBlock={1}
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    // padding: "4px 8px",
                    height: "32px",
                    backgroundColor: isSelected
                        ? hexToRGB(theme.palette.primary.main, 0.1)
                        : "transparent",
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": {
                        backgroundColor: hexToRGB(
                            theme.palette.primary.main,
                            0.12,
                        ),
                    },
                })}
                onClick={() => onSelectPath(path)}
                pl={depthPadding + (canHaveChildren ? 1 : 4)}
            >
                {canHaveChildren && (
                    <Tooltip
                        title={
                            hasChildren
                                ? ""
                                : `No ${
                                      schema.type === "object"
                                          ? "properties"
                                          : "items"
                                  } defined`
                        }
                    >
                        <ChevronRight
                            fontSize="small"
                            sx={{
                                transform: expanded
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                                transition: "transform 0.2s",
                                cursor: hasChildren ? "pointer" : "not-allowed",
                                opacity: hasChildren ? 1 : 0.5,
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hasChildren) {
                                    setExpanded(!expanded);
                                }
                            }}
                        />
                    </Tooltip>
                )}
                {typeof label !== "object" ? (
                    <Typography variant="body2">{label}</Typography>
                ) : (
                    label
                )}
                {canHaveChildren && (
                    <Box justifySelf="flex-end">
                        {isSelected && (
                            <>
                                {schema.type === "object" && onAddProperty && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAddProperty(path);
                                        }}
                                        title="Add property"
                                        sx={{ fontSize: "18px" }}
                                    >
                                        <AddIcon fontSize="inherit" />
                                    </IconButton>
                                )}
                                {schema.type === "array" &&
                                    !schema.items &&
                                    onAddItem && (
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onAddItem(path);
                                            }}
                                            title="Add item"
                                            sx={{ fontSize: "18px" }}
                                        >
                                            <AddIcon fontSize="inherit" />
                                        </IconButton>
                                    )}
                            </>
                        )}
                    </Box>
                )}
            </Box>

            {expanded && hasChildren && (
                <Box>
                    {schema.type === "object" && (
                        <>
                            {Object.entries(
                                (schema as JsonSchemaObject).properties || {},
                            ).map(([propName, propSchema]) => (
                                <TreeNode
                                    key={propName}
                                    schema={propSchema}
                                    path={[...path, propName]}
                                    label={
                                        <TreeNodeLabel
                                            type={propSchema.type}
                                            primaryText={propName}
                                            secondaryText={
                                                propSchema.title ?? "(no title)"
                                            }
                                        />
                                    }
                                    onSelectPath={onSelectPath}
                                    selectedPath={selectedPath}
                                    onAddProperty={onAddProperty}
                                    onAddItem={onAddItem}
                                    depth={depth + 1}
                                />
                            ))}
                        </>
                    )}
                    {isArrayAndHasChildren && (
                        <TreeNode
                            schema={(schema as JsonSchemaArray).items!}
                            path={[...path, "items"]}
                            label={
                                <TreeNodeLabel
                                    type={(schema as JsonSchemaArray).items!.type}
                                    primaryText={
                                        (schema as JsonSchemaArray).items!
                                            .title ?? "Items"
                                    }
                                />
                            }
                            onSelectPath={onSelectPath}
                            selectedPath={selectedPath}
                            onAddProperty={onAddProperty}
                            onAddItem={onAddItem}
                            depth={depth + 1}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
}

export function SchemaHierarchyTree({
    schema,
    selectedPath,
    onSelectPath,
    onAddProperty,
    onAddItem,
}: SchemaHierarchyTreeProps) {
    return (
        <Box sx={{ padding: 2 }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
                Schema Structure
            </Typography>
            <TreeNode
                schema={schema}
                path={[]}
                label={
                    <TreeNodeLabel
                        type={schema.type}
                        primaryText={schema.title || "Root"}
                    />
                }
                onSelectPath={onSelectPath}
                selectedPath={selectedPath}
                onAddProperty={onAddProperty}
                onAddItem={onAddItem}
            />
        </Box>
    );
}
